import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';
import { handleAuthError } from './authUtils';
import LanguageSelector from './LanguageSelector';

// BackButton component (same as in other components)
const BackButton = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  return (
    <button
      onClick={() => navigate(-1)}
      style={{
        background: 'none',
        border: 'none',
        fontSize: 24,
        cursor: 'pointer',
        color: '#888',
        padding: 8,
        borderRadius: 4,
        transition: 'color 0.2s',
        textDecoration: 'none'
      }}
      onMouseOver={(e) => e.target.style.color = '#333'}
      onMouseOut={(e) => e.target.style.color = '#888'}
      title={getTranslation('back', language)}
    >
      ←
    </button>
  );
};

// Breadcrumbs component
const Breadcrumbs = () => {
  const { language } = useLanguage();
  const location = window.location.pathname;
  const segments = location.split('/').filter(seg => seg);
  
  const crumbs = segments.map(seg => {
    const decodedSeg = decodeURIComponent(seg);
    return decodedSeg;
  });
  
  return (
    <div style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, fontSize: window.innerWidth <= 600 ? 18 : 36, color: '#1e3a8a', textAlign: 'center', letterSpacing: 1 }}>
      {crumbs.map((seg, idx) => {
        let displayText;
        if (idx === 0 && seg === 'mecz') {
          displayText = getTranslation('menu', language);
        } else if (seg === 'mecz-towarzyski') {
          displayText = getTranslation('mecz-towarzyski', language);
        } else if (seg === 'przeglad') {
          displayText = getTranslation('przeglad-meczow', language);
        } else if (seg === 'mecz') {
          // Skip the intermediate 'mecz' segment
          return null;
        } else {
          displayText = getTranslation(seg, language);
        }
        
        return (
          <span key={idx}>
            {idx > 0 && <span style={{ margin: '0 8px' }}>/</span>}
            {displayText}
          </span>
        );
      }).filter(Boolean)}
    </div>
  );
};

const FriendlyGamesReview = () => {
  const { language } = useLanguage();
  const [games, setGames] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingGame, setEditingGame] = useState(null);
  const [editData, setEditData] = useState({});
  
  // Filter states
  const [filters, setFilters] = useState({
    player2: '',
    player3: '',
    opponent1: '',
    opponent2: '',
    opponent3: '',
    dateFrom: '',
    dateTo: '',
    numberOfPlayers: '' // '1' for Single, '2' for Dublety, '3' for Triplety
  });

  // Fetch games data
  useEffect(() => {
    const fetchGames = async () => {
      try {
        const token = sessionStorage.getItem('access_token');
        console.log('Token:', token ? 'present' : 'missing');
        
        if (!token) {
          throw new Error(getTranslation('brakTokenuAutoryzacji', language));
        }
        
        const response = await fetch('/api/games/friendly', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('Response status:', response.status);
        
        if (!response.ok) {
          if (response.status === 401) {
            handleAuthError(response);
            return;
          }
          const errorText = await response.text();
          console.log('Error response:', errorText);
          throw new Error(getTranslation('bladStatus', language).replace('{status}', response.status).replace('{text}', errorText));
        }
        
        const data = await response.json();
        console.log('Games data:', data);
        setGames(data);
        setFilteredGames(data);
        setLoading(false);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchGames();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = games.filter(game => {
      // Parse game date and normalize to date only (no time)
      const gameDate = new Date(game.gamedate);
      const gameDateOnly = new Date(gameDate.getFullYear(), gameDate.getMonth(), gameDate.getDate());
      
      // Parse filter dates and normalize to date only
      const dateFrom = filters.dateFrom ? new Date(filters.dateFrom + 'T00:00:00') : null;
      const dateTo = filters.dateTo ? new Date(filters.dateTo + 'T23:59:59') : null;
      
      console.log('Filtering game:', {
        gameDate: game.gamedate,
        gameDateOnly: gameDateOnly.toISOString().split('T')[0],
        dateFrom: dateFrom ? dateFrom.toISOString().split('T')[0] : null,
        dateTo: dateTo ? dateTo.toISOString().split('T')[0] : null,
        passesDateFilter: (!dateFrom || gameDateOnly >= dateFrom) && (!dateTo || gameDateOnly <= dateTo)
      });
      
      return (
        (!filters.player2 || (game.player2 && game.player2.toLowerCase().includes(filters.player2.toLowerCase()))) &&
        (!filters.player3 || (game.player3 && game.player3.toLowerCase().includes(filters.player3.toLowerCase()))) &&
        (!filters.opponent1 || (game.opponent1 && game.opponent1.toLowerCase().includes(filters.opponent1.toLowerCase()))) &&
        (!filters.opponent2 || (game.opponent2 && game.opponent2.toLowerCase().includes(filters.opponent2.toLowerCase()))) &&
        (!filters.opponent3 || (game.opponent3 && game.opponent3.toLowerCase().includes(filters.opponent3.toLowerCase()))) &&
        (!dateFrom || gameDateOnly >= dateFrom) &&
        (!dateTo || gameDateOnly <= dateTo) &&
        (!filters.numberOfPlayers || game.numberofplayers.toString() === filters.numberOfPlayers)
      );
    });
    
    setFilteredGames(filtered);
  }, [games, filters]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle edit start
  const handleEdit = (game) => {
    setEditingGame(game.game_id);
    setEditData({
      player2: game.player2 || '',
      player3: game.player3 || '',
      opponent1: game.opponent1 || '',
      opponent2: game.opponent2 || '',
      opponent3: game.opponent3 || '',
      playerscore: game.playerscore,
      opponentscore: game.opponentscore
    });
  };

  // Handle edit data change
  const handleEditDataChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle save
  const handleSave = async (gameId) => {
    try {
      const token = sessionStorage.getItem('access_token');
      console.log('Save token:', token ? 'present' : 'missing');
      console.log('Save data:', editData);
      
      if (!token) {
        throw new Error(getTranslation('brakTokenuAutoryzacji', language));
      }
      
      const response = await fetch(`/api/games/${gameId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });
      
      console.log('Save response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('Save error response:', errorText);
        throw new Error(getTranslation('bladStatus', language).replace('{status}', response.status).replace('{text}', errorText));
      }
      
      const result = await response.json();
      console.log('Save result:', result);
      
      // Update local state
      setGames(prev => prev.map(game => 
        game.game_id === gameId 
          ? { ...game, ...editData }
          : game
      ));
      
      setEditingGame(null);
      setEditData({});
    } catch (err) {
      console.error('Save error:', err);
      alert(err.message);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setEditingGame(null);
    setEditData({});
  };

  // Handle delete
  const handleDelete = async (gameId) => {
    if (!window.confirm(getTranslation('czy-na-pewno-usunac-mecz', language))) {
      return;
    }

    try {
      const token = sessionStorage.getItem('access_token');
      console.log('Delete token:', token ? 'present' : 'missing');
      
      if (!token) {
        throw new Error(getTranslation('brakTokenuAutoryzacji', language));
      }
      
      const response = await fetch(`/api/games/${gameId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Delete response status:', response.status);
      
      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError(response);
          return;
        }
        const errorText = await response.text();
        console.log('Delete error response:', errorText);
        throw new Error(getTranslation('bladStatus', language).replace('{status}', response.status).replace('{text}', errorText));
      }
      
      const result = await response.json();
      console.log('Delete result:', result);
      
      // Update local state by removing the deleted game
      setGames(prev => prev.filter(game => game.game_id !== gameId));
      
    } catch (err) {
      console.error('Delete error:', err);
      alert(err.message);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          ShootMe
        </h1>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>{getTranslation('ladowanieDanychReview', language)}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          ShootMe
        </h1>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'red' }}>{getTranslation('bladReview', language).replace('{error}', error)}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
      {/* Language Selector - Top Right */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <LanguageSelector />
      </div>

      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton />
        <Breadcrumbs />
      </div>
      
      {/* Filters */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        maxWidth: '1200px',
        margin: '0 auto 20px auto'
      }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#1e3a8a', fontFamily: 'Nunito, Arial, sans-serif' }}>
          {getTranslation('filtry', language)}
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '15px' 
        }}>
          <input
            type="text"
            placeholder={getTranslation('gracz2', language)}
            value={filters.player2}
            onChange={(e) => handleFilterChange('player2', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
          />
          <input
            type="text"
            placeholder={getTranslation('gracz3', language)}
            value={filters.player3}
            onChange={(e) => handleFilterChange('player3', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
          />
          <input
            type="text"
            placeholder={getTranslation('przeciwnik1', language)}
            value={filters.opponent1}
            onChange={(e) => handleFilterChange('opponent1', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
          />
          <input
            type="text"
            placeholder={getTranslation('przeciwnik2', language)}
            value={filters.opponent2}
            onChange={(e) => handleFilterChange('opponent2', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
          />
          <input
            type="text"
            placeholder={getTranslation('przeciwnik3', language)}
            value={filters.opponent3}
            onChange={(e) => handleFilterChange('opponent3', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
          />
          <input
            type="date"
            placeholder={getTranslation('dataOd', language)}
            value={filters.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
          />
          <input
            type="date"
            placeholder={getTranslation('dataDo', language)}
            value={filters.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
          />
        </div>
        
        {/* Number of Players Radio Buttons */}
        <div style={{ 
          marginTop: '20px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <h4 style={{ 
            margin: '0 0 15px 0', 
            color: '#1e3a8a', 
            fontFamily: 'Nunito, Arial, sans-serif',
            fontSize: '16px'
          }}>
            {getTranslation('liczbaGraczy', language)}
          </h4>
          <div style={{ 
            display: 'flex', 
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              <input
                type="radio"
                name="numberOfPlayers"
                value=""
                checked={filters.numberOfPlayers === ''}
                onChange={(e) => handleFilterChange('numberOfPlayers', e.target.value)}
                style={{ margin: 0 }}
              />
              {getTranslation('wszystkie', language)}
            </label>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              <input
                type="radio"
                name="numberOfPlayers"
                value="1"
                checked={filters.numberOfPlayers === '1'}
                onChange={(e) => handleFilterChange('numberOfPlayers', e.target.value)}
                style={{ margin: 0 }}
              />
              {getTranslation('single', language)}
            </label>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              <input
                type="radio"
                name="numberOfPlayers"
                value="2"
                checked={filters.numberOfPlayers === '2'}
                onChange={(e) => handleFilterChange('numberOfPlayers', e.target.value)}
                style={{ margin: 0 }}
              />
              {getTranslation('dublety', language)}
            </label>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              <input
                type="radio"
                name="numberOfPlayers"
                value="3"
                checked={filters.numberOfPlayers === '3'}
                onChange={(e) => handleFilterChange('numberOfPlayers', e.target.value)}
                style={{ margin: 0 }}
              />
              {getTranslation('triplety', language)}
            </label>
          </div>
        </div>
      </div>
      
      {/* Table */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        maxWidth: '1200px',
        margin: '0 auto',
        overflowX: 'auto'
      }}>
        {filteredGames.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666', fontFamily: 'Nunito, Arial, sans-serif' }}>
            {getTranslation('brakMeczyKryteria', language)}
          </p>
        ) : (
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            fontFamily: 'Nunito, Arial, sans-serif',
            fontSize: '14px'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('dataICzas', language)}</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('gracz1', language)}</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('gracz2', language)}</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('gracz3', language)}</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>{getTranslation('wynik', language)}</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('przeciwnik1', language)}</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('przeciwnik2', language)}</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('przeciwnik3', language)}</th>
                <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('akcje', language)}</th>
              </tr>
            </thead>
            <tbody>
              {filteredGames.map((game) => (
                <tr key={game.game_id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px 8px' }}>{formatDate(game.gamedate)}</td>
                  <td style={{ padding: '12px 8px' }}>{game.player1_name}</td>
                  <td style={{ padding: '12px 8px' }}>
                    {editingGame === game.game_id ? (
                      <input
                        type="text"
                        value={editData.player2}
                        onChange={(e) => handleEditDataChange('player2', e.target.value)}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px',
                          width: '100%'
                        }}
                      />
                    ) : (
                      game.player2 || '-'
                    )}
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    {editingGame === game.game_id ? (
                      <input
                        type="text"
                        value={editData.player3}
                        onChange={(e) => handleEditDataChange('player3', e.target.value)}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px',
                          width: '100%'
                        }}
                      />
                    ) : (
                      game.player3 || '-'
                    )}
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                    {editingGame === game.game_id ? (
                      <div style={{ display: 'flex', gap: '5px', alignItems: 'center', justifyContent: 'center' }}>
                        <input
                          type="number"
                          value={editData.playerscore}
                          onChange={(e) => handleEditDataChange('playerscore', parseInt(e.target.value) || 0)}
                          style={{
                            padding: '4px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '12px',
                            width: '50px'
                          }}
                        />
                        <span>:</span>
                        <input
                          type="number"
                          value={editData.opponentscore}
                          onChange={(e) => handleEditDataChange('opponentscore', parseInt(e.target.value) || 0)}
                          style={{
                            padding: '4px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '12px',
                            width: '50px'
                          }}
                        />
                      </div>
                    ) : (
                      <span style={{ 
                        fontWeight: 'bold',
                        color: game.playerscore > game.opponentscore ? '#2e7d32' : 
                               game.playerscore < game.opponentscore ? '#c62828' : '#666'
                      }}>
                        {game.playerscore} : {game.opponentscore}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    {editingGame === game.game_id ? (
                      <input
                        type="text"
                        value={editData.opponent1}
                        onChange={(e) => handleEditDataChange('opponent1', e.target.value)}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px',
                          width: '100%'
                        }}
                      />
                    ) : (
                      game.opponent1 || '-'
                    )}
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    {editingGame === game.game_id ? (
                      <input
                        type="text"
                        value={editData.opponent2}
                        onChange={(e) => handleEditDataChange('opponent2', e.target.value)}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px',
                          width: '100%'
                        }}
                      />
                    ) : (
                      game.opponent2 || '-'
                    )}
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    {editingGame === game.game_id ? (
                      <input
                        type="text"
                        value={editData.opponent3}
                        onChange={(e) => handleEditDataChange('opponent3', e.target.value)}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px',
                          width: '100%'
                        }}
                      />
                    ) : (
                      game.opponent3 || '-'
                    )}
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    {editingGame === game.game_id ? (
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button
                          onClick={() => handleSave(game.game_id)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          {getTranslation('zapisz', language)}
                        </button>
                        <button
                          onClick={handleCancel}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          {getTranslation('anuluj', language)}
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button
                          onClick={() => handleEdit(game)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          {getTranslation('edytuj', language)}
                        </button>
                        <button
                          onClick={() => handleDelete(game.game_id)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          {getTranslation('usun', language)}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default FriendlyGamesReview;
